import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Handshake, BellRing, Landmark, Wrench, LayoutList, Bell, LayoutDashboard, ShoppingCart, ClipboardList, PackageCheck, Truck, Send, Factory, Warehouse, Store, Boxes, BookOpen, ClipboardCheck, ScrollText, TrendingDown, Wallet, Receipt, BarChart3, Settings, ShieldCheck, Upload, Menu, LogOut, SlidersHorizontal, AlertTriangle, BadgeCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }, { to: "/review", label: "Perlu Review", icon: BadgeCheck, perm: ["purchase.verify", "transfer.verify"] }, { to: "/reminders", label: "Pengingat", icon: BellRing }] },
  { label: "Finance", area: "area.finance", items: [
    { to: "/purchases", label: "Pembelian", icon: ShoppingCart, perm: ["purchase.view", "purchase.create"] },
    { to: "/payables", label: "Hutang Supplier", icon: Landmark, perm: ["payable.pay"] },
    { to: "/consignment-recap", label: "Rekap Konsinyasi", icon: Handshake, perm: ["consignment.settle"] },
    { to: "/purchase-requests", label: "Permintaan Pembelian", icon: ClipboardList },
    { to: "/cash", label: "Akun Kas", icon: Wallet, perm: ["cash.manage", "cash.outlet", "payable.pay"] },
    { to: "/expenses", label: "Biaya Outlet", icon: Receipt, perm: ["expense.create", "reports.view"] },
    { to: "/reports/pnl", label: "Laporan", icon: BarChart3, perm: ["reports.view"] },
  ] },
  { label: "Gudang", area: "area.warehouse", items: [
    { to: "/receiving", label: "Penerimaan", icon: PackageCheck },
    { to: "/inventory/warehouse", label: "Stok Gudang", icon: Warehouse, perm: ["inventory.view"] },
    { to: "/transfers", label: "Transfer / DO", icon: Truck },
    { to: "/delivery-requests", label: "Permintaan Pengiriman", icon: Send },
    { to: "/production", label: "Produksi", icon: Factory, perm: ["production.do"] },
    { to: "/opname", label: "Stock Opname", icon: ClipboardCheck, perm: ["opname.do", "opname.complete"] },
    { to: "/consignment", label: "Konsinyasi", icon: Handshake, perm: ["consignment.do", "consignment.verify"] },
    { to: "/adjustments", label: "Penyesuaian", icon: SlidersHorizontal, perm: ["adjust.do"] },
  ] },
  { label: "Outlet", area: "area.outlet", items: [
    { to: "/receiving", label: "Penerimaan Barang", icon: PackageCheck },
    { to: "/delivery-requests", label: "Permintaan Pengiriman", icon: Send },
    { to: "/inventory/outlets", label: "Stok Outlet", icon: Store, perm: ["inventory.view"] },
    { to: "/opname", label: "Stock Opname", icon: ClipboardCheck, perm: ["opname.do", "opname.complete"] },
    { to: "/consignment", label: "Konsinyasi", icon: Handshake, perm: ["consignment.do", "consignment.verify"] },
    { to: "/purchase-requests", label: "Permintaan Pembelian", icon: ClipboardList, perm: ["request.manage"] },
  ] },
  { label: "General Affair", area: "area.ga", items: [
    { to: "/assets", label: "Aset & Perawatan", icon: Wrench, perm: ["ga.view", "ga.manage"] },
  ] },
  { label: "Master & Kontrol", area: "area.master", items: [
    { to: "/items", label: "Barang & Konversi", icon: Boxes },
    { to: "/recipes", label: "Resep / BOM", icon: BookOpen },
    { to: "/opname/templates", label: "Template Opname", icon: LayoutList, perm: ["opname.template"] },
    { to: "/ledger", label: "Jurnal Stok", icon: ScrollText, perm: ["inventory.view"] },
    { to: "/inventory/variance", label: "Variansi", icon: TrendingDown, perm: ["opname.view_expected"] },
    { to: "/sales-import", label: "Impor Penjualan POS", icon: Upload, perm: ["sales.import"] },
    { to: "/settings/users", label: "Pengaturan", icon: Settings, perm: ["users.manage", "master.manage"] },
    { to: "/audit", label: "Audit Log", icon: ShieldCheck, perm: ["audit.view"] },
  ] },
];

export const useNav = () => {
  const { can } = useAuth();
  const seen = new Set();
  return NAV.map((g) => ({ ...g, items: (!g.area || can(g.area)) ? g.items.filter((i) => (!i.perm || can(...i.perm)) && !seen.has(i.to) && seen.add(i.to)) : [] }));
};

const segOf = (p) => p.split("/")[1] || "";

const SideNav = ({ onNav }) => {
  const groups = useNav();
  return (
    <nav className="flex-1 overflow-y-auto px-3 pb-6">
      {groups.map((g, gi) => {
        const items = g.items;
        if (!items.length) return null;
        return (
          <div key={gi} className="mt-4">
            {g.label && <div className="px-2 mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{g.label}</div>}
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end} onClick={onNav} data-testid={`nav-${i.to.replace(/\//g, "-").slice(1) || "dashboard"}`}
                className={({ isActive }) => `flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[13px] font-medium transition-colors ${isActive ? "bg-emerald-500/15 text-emerald-300" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
                <i.icon size={15} /> {i.label}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
};

const Brand = () => (
  <div className="px-5 py-5 border-b border-white/10">
    <div className="text-white text-xl font-extrabold tracking-[0.2em]">MŌNA</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-semibold mt-0.5">Operations Control</div>
  </div>
);

const AttentionBell = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => { api.get("/attention").then((r) => setItems(r.data)).catch(() => {}); }, [open]);
  const sev = { danger: "text-rose-600", warning: "text-amber-600", info: "text-sky-600" };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-slate-100" data-testid="attention-bell">
          <Bell size={18} />
          {items.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] grid place-items-center px-1">{items.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="px-4 py-2.5 border-b text-sm font-semibold">Perlu perhatian</div>
        <div className="max-h-96 overflow-y-auto divide-y">
          {items.length === 0 && <div className="p-4 text-sm text-slate-500">Semua beres.</div>}
          {items.map((a, i) => (
            <button key={i} onClick={() => { setOpen(false); nav(a.link); }} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex gap-2 text-[13px]" data-testid={`attention-item-${i}`}>
              <AlertTriangle size={14} className={`mt-0.5 shrink-0 ${sev[a.severity]}`} /> {a.title}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const [mob, setMob] = useState(false);
  const groups = useNav();
  const { pathname } = useLocation();
  const allowed = new Set(groups.flatMap((g) => g.items.map((i) => segOf(i.to))));
  const ok = pathname === "/" || allowed.has(segOf(pathname));
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-60 shrink-0 bg-slate-950 flex-col fixed inset-y-0"><Brand /><SideNav /></aside>
      <Sheet open={mob} onOpenChange={setMob}>
        <SheetContent side="left" className="p-0 w-64 bg-slate-950 border-0 flex flex-col"><Brand /><SideNav onNav={() => setMob(false)} /></SheetContent>
      </Sheet>
      <div className="flex-1 lg:ml-60 min-w-0">
        <header className="sticky top-0 z-30 h-14 bg-white/85 backdrop-blur-md border-b border-slate-200 flex items-center px-4 gap-3">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setMob(true)} data-testid="mobile-menu-button"><Menu size={20} /></button>
          <div className="flex-1" />
          <AttentionBell />
          <div className="text-right leading-tight">
            <div className="text-[13px] font-semibold" data-testid="current-user-name">{user.name}</div>
            <div className="text-[11px] text-slate-500">{user.role?.name}</div>
          </div>
          <button onClick={logout} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" title="Keluar" data-testid="logout-button"><LogOut size={17} /></button>
        </header>
        <main className="p-4 lg:p-6 max-w-[1600px]">{ok ? <Outlet /> : <div className="bg-white border rounded-lg p-10 text-center text-sm text-slate-500" data-testid="no-access">Halaman ini tidak termasuk akses peran Anda.</div>}</main>
      </div>
    </div>
  );
}
